// Figma frame 18 · Settings (33:845) — US-131.
//
// The drawn frame carries four cards: ORGANISATION (rename), TEAM (invite),
// EMAIL (report prefs + a second "coverage drops" alert toggle), DANGER ZONE
// (delete org). Only the report-recipient piece of EMAIL is backed by
// anything `setup-backend` built this wave (`PUT /licenses/report-prefs`
// reads/writes the license row's existing `contact_email` — no new column).
// Renaming an org, inviting a teammate, a second alert type and deleting an
// org all have no endpoint here, and the story's own acceptance criterion is
// explicit: "no new field is invented in the UI that the backend cannot
// store." Building fake controls for those would be worse than not drawing
// them — a Save button that silently does nothing is the exact defect this
// story also forbids ("Every save reports success or failure; a silent save
// is a defect"). ORGANISATION and REPORT PREFERENCES below are real; TEAM
// and DANGER ZONE are omitted and flagged in the progress notes for whoever
// picks up self-service org management.
//
// LICENSE KEYS (added after the lifecycle audit — ORCHESTRATOR_NOTES §7).
// This card is the answer to "a key was shown once, is unrecoverable, and
// leaked" — a gap that previously had no remedy but a support ticket. Three
// things about it are load-bearing and must survive any redesign:
//
//  1. NO KEY IS EVER RENDERED FROM A READ. The table shows `key_id`, the
//     backend's sha256 fingerprint, plus plan/domains/created/status. There
//     is no masked field and no reveal control, because there is nothing
//     behind them: only sha256(key) is stored. A UI that appeared to show an
//     existing key would either be lying or mean someone added a plaintext
//     column.
//  2. The one plaintext key lives in `minted`, local component state fed by
//     the mint mutation's response. It is never cached, never refetched, and
//     is gone on navigation — which is what makes the "we cannot show this
//     again" notice true rather than decorative.
//  3. Mint and revoke are HUMAN actions on a MACHINE credential, gated on
//     the session cookie. Nothing here accepts a key as input; there is
//     deliberately no key-ENTRY field anywhere in this app.

import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Skeleton,
  Toast,
  Toggle,
} from "../../ui";
import { LicenseKeysCard } from "./LicenseKeysCard";
import { useLicense, useUpdateReportPrefs } from "./api";
import "./ops.css";

function planLabel(plan: string | null): string {
  if (!plan) return "No plan on file";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

export function SettingsScreen({ orgSlug }: { orgSlug: string }) {
  const license = useLicense();
  const updatePrefs = useUpdateReportPrefs();

  const [email, setEmail] = useState("");
  const [toast, setToast] = useState<{ tone: "nominal" | "danger"; message: string } | null>(null);

  // Seed the editable field from the server once it loads — never overwrite
  // an in-progress edit on a background refetch.
  useEffect(() => {
    if (license.data && email === "") {
      setEmail(license.data.contact_email ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [license.data]);

  const subscribed = license.data ? license.data.contact_email !== null : false;

  function save(nextEmail: string | null) {
    updatePrefs.mutate(nextEmail, {
      onSuccess: () => {
        setToast({
          tone: "nominal",
          message: nextEmail ? "Report recipient saved." : "Unsubscribed from the monthly report.",
        });
      },
      onError: (err) => {
        setToast({ tone: "danger", message: err.message || "Couldn't save that change." });
      },
    });
  }

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Settings"
        description={`Manage how mAiSight reaches ${orgSlug}.`}
      />

      <div className="maisight-ops-body">
        <Card header="Organisation">
          {license.data ? (
            <>
              <div className="maisight-ops-kv">
                <span className="type-labelSm maisight-ops-kv-label">Workspace</span>
                <span className="type-bodyS maisight-ops-kv-value">{license.data.org_slug}</span>
              </div>
              <div className="maisight-ops-kv">
                <span className="type-labelSm maisight-ops-kv-label">Plan</span>
                <span className="type-bodyS maisight-ops-kv-value">{planLabel(license.data.plan)}</span>
              </div>
              <div className="maisight-ops-kv">
                <span className="type-labelSm maisight-ops-kv-label">Status</span>
                <Badge tone={license.data.resolved_state === "active" ? "nominal" : "neutral"} lamp>
                  {license.data.resolved_state}
                </Badge>
              </div>
            </>
          ) : license.isError ? (
            <ErrorState headline="Couldn't load your organisation" detail={license.error?.message} />
          ) : (
            <Skeleton width={280} height={16} />
          )}
        </Card>

        <LicenseKeysCard />

        <Card header="Monthly report">
          <div className="maisight-ops-form">
            <div className="maisight-ops-toggle-row">
              <Toggle
                checked={subscribed}
                disabled={updatePrefs.isPending || !license.data}
                onChange={(checked) => {
                  if (!checked) {
                    save(null);
                    return;
                  }
                  // Turning it on needs an address to send to; the field
                  // below is where that gets typed, so opening it here
                  // doesn't yet call the mutation.
                  if (email.trim() === "") return;
                  save(email.trim());
                }}
                label="Send the monthly vocabulary-health report"
              />
            </div>
            <p className="type-bodyS maisight-ops-toggle-copy">
              {subscribed
                ? "Sent once a month to the address below."
                : "No one currently receives this report."}
            </p>
            <Field label="Recipient email" id="report-recipient-email">
              <Input
                type="email"
                value={email}
                placeholder="you@yourcompany.com"
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <div className="maisight-ops-actions">
              <Button
                variant="secondary"
                disabled={updatePrefs.isPending || !license.data}
                onClick={() => save(null)}
              >
                Unsubscribe
              </Button>
              <Button
                variant="primary"
                disabled={updatePrefs.isPending || !license.data || email.trim() === ""}
                onClick={() => save(email.trim())}
              >
                Save recipient
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {toast ? (
        <div className="maisight-ops-toast-slot" data-chassis="paper">
          <Toast title={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
        </div>
      ) : null}
    </>
  );
}
